/**
 * Native Cognito auth — no Hosted UI redirect.
 *
 * Uses amazon-cognito-identity-js to call the User Pool API directly
 * from the React app. Passwords go over SRP (Secure Remote Password)
 * so they're never sent in clear. We keep `idToken` in localStorage
 * for API calls.
 *
 * The exposed AuthContext lets the rest of the app:
 *   - Read `me` (profile + scopes loaded from /me after login)
 *   - Call `signIn`, `signUp`, `confirm`, `resend`, `forgot`,
 *     `confirmForgot`, `signOut` directly with email + password.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession,
  ICognitoUserPoolData,
} from "amazon-cognito-identity-js";

const REGION = "eu-west-2";
const USER_POOL_ID = import.meta.env.VITE_USER_POOL_ID || "";
const CLIENT_ID = import.meta.env.VITE_USER_POOL_CLIENT_ID || "";

const poolData: ICognitoUserPoolData = {
  UserPoolId: USER_POOL_ID,
  ClientId: CLIENT_ID,
};
const userPool = new CognitoUserPool(poolData);

export type Me = {
  email: string;
  is_superuser: boolean;
  scopes: string[];
  unit_scopes: string[];
};

type AuthAPI = {
  me: Me | null;
  idToken: string | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, givenName?: string) => Promise<void>;
  confirm: (email: string, code: string) => Promise<void>;
  resendCode: (email: string) => Promise<void>;
  forgot: (email: string) => Promise<void>;
  confirmForgot: (email: string, code: string, newPassword: string) => Promise<void>;
  signOut: () => void;
};

const Ctx = createContext<AuthAPI>({} as any);
export const useAuth = () => useContext(Ctx);

const TOKEN_KEY = "id_token";

function persistSession(session: CognitoUserSession) {
  localStorage.setItem(TOKEN_KEY, session.getIdToken().getJwtToken());
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [idToken, setIdToken] = useState<string | null>(localStorage.getItem(TOKEN_KEY));
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  // On boot, try to restore an active Cognito session — refreshes the
  // idToken if expired (since refresh tokens live ~30 days by default).
  useEffect(() => {
    const cu = userPool.getCurrentUser();
    if (!cu) {
      setLoading(false);
      return;
    }
    cu.getSession((err: any, session: CognitoUserSession | null) => {
      if (!err && session?.isValid()) {
        persistSession(session);
        setIdToken(session.getIdToken().getJwtToken());
      } else {
        localStorage.removeItem(TOKEN_KEY);
        setIdToken(null);
      }
      setLoading(false);
    });
  }, []);

  // Fetch /me whenever we hold a token.
  useEffect(() => {
    if (!idToken) {
      setMe(null);
      return;
    }
    fetch(`${import.meta.env.VITE_API_URL}/me`, {
      headers: { Authorization: `Bearer ${idToken}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setMe(d as Me | null))
      .catch(() => setMe(null));
  }, [idToken]);

  const signIn = useCallback((email: string, password: string) => {
    const user = new CognitoUser({ Username: email.toLowerCase(), Pool: userPool });
    const auth = new AuthenticationDetails({ Username: email.toLowerCase(), Password: password });
    return new Promise<void>((resolve, reject) => {
      user.authenticateUser(auth, {
        onSuccess: (session) => {
          persistSession(session);
          setIdToken(session.getIdToken().getJwtToken());
          resolve();
        },
        onFailure: (err) => reject(err),
      });
    });
  }, []);

  const signUp = useCallback((email: string, password: string, givenName?: string) => {
    const attrs: CognitoUserAttribute[] = [
      new CognitoUserAttribute({ Name: "email", Value: email.toLowerCase() }),
    ];
    if (givenName) {
      attrs.push(new CognitoUserAttribute({ Name: "given_name", Value: givenName }));
    }
    return new Promise<void>((resolve, reject) => {
      userPool.signUp(email.toLowerCase(), password, attrs, [], (err) => {
        err ? reject(err) : resolve();
      });
    });
  }, []);

  const confirm = useCallback((email: string, code: string) => {
    const user = new CognitoUser({ Username: email.toLowerCase(), Pool: userPool });
    return new Promise<void>((resolve, reject) => {
      user.confirmRegistration(code, true, (err) => (err ? reject(err) : resolve()));
    });
  }, []);

  const resendCode = useCallback((email: string) => {
    const user = new CognitoUser({ Username: email.toLowerCase(), Pool: userPool });
    return new Promise<void>((resolve, reject) => {
      user.resendConfirmationCode((err) => (err ? reject(err) : resolve()));
    });
  }, []);

  const forgot = useCallback((email: string) => {
    const user = new CognitoUser({ Username: email.toLowerCase(), Pool: userPool });
    return new Promise<void>((resolve, reject) => {
      user.forgotPassword({
        onSuccess: () => resolve(),
        onFailure: (err) => reject(err),
      });
    });
  }, []);

  const confirmForgot = useCallback((email: string, code: string, newPassword: string) => {
    const user = new CognitoUser({ Username: email.toLowerCase(), Pool: userPool });
    return new Promise<void>((resolve, reject) => {
      user.confirmPassword(code, newPassword, {
        onSuccess: () => resolve(),
        onFailure: (err) => reject(err),
      });
    });
  }, []);

  const signOut = useCallback(() => {
    const cu = userPool.getCurrentUser();
    if (cu) cu.signOut();
    localStorage.removeItem(TOKEN_KEY);
    setIdToken(null);
    setMe(null);
  }, []);

  return (
    <Ctx.Provider value={{ me, idToken, loading, signIn, signUp, confirm, resendCode, forgot, confirmForgot, signOut }}>
      {children}
    </Ctx.Provider>
  );
}

/** Authorized fetch helper — same API as before. */
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const tok = localStorage.getItem(TOKEN_KEY);
  if (tok) headers.set("Authorization", `Bearer ${tok}`);
  const res = await fetch(`${import.meta.env.VITE_API_URL}${path}`, { ...init, headers });
  if (res.status === 401) {
    localStorage.removeItem(TOKEN_KEY);
    window.location.href = "/";
    throw new Error("unauthorised");
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}
